import React, { useContext, useEffect, useRef, useState } from "react";
import NavSub from "../components/NavSub";
import "../Styles/QualityControl.css";
import "../Styles/common.css";
import { AuthContext } from "../context/AuthContext";
import { Box, FormControl, MenuItem, Modal, Select, TextField } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import isGuestUser, { hasRole, GUEST_USER_ROLE_ID } from "../utils/guestUser";
import { XCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { taskApiUrl, userApiUrl } from "../utils/GetUrl";
import ToastComponent from "../components/ToastComponent";

// Roles allowed to manage control points. The "Control Point Setup" menu item
// is already restricted to these roles in the DB (menu_role_mapping), so the
// menu link is only shown to them. This guard is defense-in-depth for anyone
// who reaches the route directly by URL.
const SETUP_ROLES = ["Admin", "Super Admin", "Managing Director"];

const ControlPointSetup = () => {
  const navigate = useNavigate();
  const toastRef = useRef();

  const { token, userData, handleAuthExpiry } = useContext(AuthContext);

  const [qcPoints, setQcPoints] = useState([]);
  const [qcPoints2, setQcPoints2] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Select Status");
  const [statuses, setStatuses] = useState({});
  const [roles, setRoles] = useState([]);

  const initialQC = {
    controlPoint: "",
    keycontrolsfor: "",
    controlDescription: "",
    tat: "",
    accuracy: "",
    frequency: "",
    criticality: "",
    controlResponsible: "",
  };
  const [qc, setQC] = useState(initialQC);
  const [open, setOpen] = useState(false);
  const [operation, setOperation] = useState("Add");
  const [selectedId, setSelectedId] = useState();

  const drName = userData?.[0]?.DR_Name;
  const isAllowed = SETUP_ROLES.some((role) => hasRole(drName, role));

  // Redirect users who reach this route without an allowed role.
  useEffect(() => {
    if (userData && !isAllowed) {
      navigate("/QualityControl", { replace: true });
    }
  }, [userData, isAllowed, navigate]);

  const handleChange = (event) => {
    if (event.target.value === "All") {
      setSelectedStatus("All");
      setQcPoints2(qcPoints);
    } else {
      setSelectedStatus(event.target.value);
    }
  };

  useEffect(() => {
    if (selectedStatus === "All") return;
    setQcPoints2(
      qcPoints?.filter((item) => item.controlResponsible === selectedStatus)
    );
  }, [selectedStatus]);

  const handleStatusChange2 = async (item, id, event) => {
    const newStatus = event.target.value;
    setStatuses((prevStatuses) => ({ ...prevStatuses, [id]: newStatus }));
    await editQcStatus2(item, id, newStatus);
  };

  const fetchRoles = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${userApiUrl}/GetRole?`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.status === 401) {
        await handleAuthExpiry();
        return;
      }
      if (!response.ok) {
        toastRef.current.showToast("Something went wrong!", "error");
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setRoles(data);
      }
    } catch (err) {
      console.error("fetchRoles error:", err);
    }
  };

  const fetchQcPoints = async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${taskApiUrl}/GetControlPointByStatus?status=all`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.status === 401) {
        await handleAuthExpiry();
        return;
      }
      if (!response.ok) {
        toastRef.current.showToast("Something went wrong!", "error");
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setQcPoints(data);
        setQcPoints2(
          selectedStatus !== "Select Status"
            ? data.filter((item) => item?.controlResponsible === selectedStatus)
            : data
        );
        const initialStatuses = data.reduce((acc, item) => {
          acc[item.id] = item.status;
          return acc;
        }, {});
        setStatuses(initialStatuses);
      } else {
        toastRef.current.showToast("Something went wrong!", "error");
      }
    } catch (err) {
      console.error("fetchQcPoints error:", err);
    }
  };

  const addQc = async () => {
    if (isGuestUser(userData?.[0]?.DRM_ID)) {
      toastRef.current.showToast(`Guest User Does not have Add or Edit Access`);
      return;
    }
    const response = await fetch(`${taskApiUrl}/CreateControlPoint`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...qc }),
    });
    if (response?.status === 401) {
      await handleAuthExpiry();
      return;
    }
    const data = await response.json();
    if (data) {
      handleClose();
      fetchQcPoints();
      toastRef.current.showToast(
        "Control Point Added Successfully!",
        "success"
      );
    }
  };

  const editQc = async () => {
    if (isGuestUser(userData?.[0]?.DRM_ID)) {
      toastRef.current.showToast(`Guest User Does not have Add or Edit Access`);
      return;
    }
    const response = await fetch(`${taskApiUrl}/UpdatecontrolPoint`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...qc, ID: selectedId }),
    });
    if (response?.status === 401) {
      await handleAuthExpiry();
      return;
    }
    const data = await response.json();
    if (data) {
      handleClose();
      setQC(initialQC);
      fetchQcPoints();
      toastRef.current.showToast(
        "Control Point Saved Successfully!",
        "success"
      );
    }
  };

  const editQcStatus2 = async (item, id, status) => {
    if (isGuestUser(userData?.[0]?.DRM_ID)) {
      toastRef.current.showToast(`Guest User Does not have Add or Edit Access`);
      return;
    }
    const currStatus = status === "P" ? "P" : status === "C" ? "C" : "D";
    const response = await fetch(`${taskApiUrl}/UpdateControlPoint`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...item, status: currStatus }),
    });
    if (response?.status === 401) {
      await handleAuthExpiry();
      return;
    }
    const data = await response.json();
    if (data) {
      fetchQcPoints();
      toastRef.current.showToast(
        "Control Point Saved Successfully!",
        "success"
      );
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchQcPoints();
  }, [token]);

  const handleOpen = (op) => {
    setOpen(true);
    if (op === "Add") {
      setQC(initialQC);
    }
  };
  const handleClose = () => setOpen(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 400,
    maxWidth: 1000,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 12,
    p: 4,
  };

  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setQC({ ...qc, [name]: value });
  };

  const handleDropdownChange = (e, field) => {
    const { value } = e.target;
    setQC({ ...qc, [field]: value });
  };

  // Only actual roles (Type "R"), excluding the Guest role, are valid for
  // Control Responsible and the filter dropdown.
  const responsibleRoles = roles?.filter(
    (role) => role.Type === "R" && role.Id !== GUEST_USER_ROLE_ID
  );

  if (!isAllowed) return null;

  return (
    <div>
      <NavSub />
      <ToastComponent ref={toastRef} timeout={4000} />

      <div>
        <div className="qc-status-bar">
          <div className="" onClick={() => navigate("/QualityControl")} style={{ cursor: "pointer" }}>
            Back to View
          </div>
          <Box sx={{ width: 150 }}>
            <FormControl fullWidth>
              <Select value={selectedStatus} onChange={handleChange}>
                <MenuItem value={"All"}>All</MenuItem>
                {responsibleRoles?.map((role) => (
                  <MenuItem key={role.Id} value={role.Name}>
                    {role.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <button
            className="common_btn"
            onClick={() => {
              setOperation("Add");
              handleOpen("Add");
            }}
          >
            + Add New
          </button>
        </div>

        {qcPoints2 && qcPoints2.length > 0 ? (
          qcPoints2.map((item) => {
            if (item?.status === "D") return null;
            return (
              <div
                className="qc-status-card"
                key={item?.id}
                style={{
                  borderLeft: `5px solid ${
                    item?.status === "P"
                      ? "rgba(239, 160, 41, 1)"
                      : item?.status === "C"
                      ? "green"
                      : item?.status === "Not Applicable"
                      ? "red"
                      : item?.status === "deleted"
                      ? "gray"
                      : "black"
                  }`,
                }}
              >
                <div className="qc-card-desc">
                  <div className="qc-card-control-points">
                    <div className="qc-card-control-point">
                      <div className="qc-card-bold-head">Control Points</div>
                      <div>{item?.controlPoint}</div>
                    </div>
                    <div>
                      <div className="qc-card-bold-head">Key Control</div>
                      <div>{item?.keycontrolsfor}</div>
                    </div>
                  </div>
                  <div className="qc-card-control-description">
                    <div className="qc-card-bold-head">Control Description</div>
                    <div>{item?.controlDescription}</div>
                  </div>
                  <div className="qc-card-control-stats">
                    <div className="qc-card-control-stat">
                      <div className="qc-card-bold-head">TAT :</div>
                      <div>{item?.tat}</div>
                    </div>
                    <div className="qc-card-control-stat">
                      <div className="qc-card-bold-head">Accuracy :</div>
                      <div>{item?.accuracy}</div>
                    </div>
                    <div className="qc-card-control-stat">
                      <div className="qc-card-bold-head">Frequency :</div>
                      <div>{item?.frequency}</div>
                    </div>
                    <div className="qc-card-control-stat">
                      <div className="qc-card-bold-head">Criticality :</div>
                      <div>{item?.criticality}</div>
                    </div>
                  </div>
                </div>
                {!(item?.status === "deleted") && (
                  <div className="qc-card-action">
                    <div className="qc-card-update-delete">
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedId(item?.id);
                          setOperation("Delete");
                          setQC(item);
                          handleOpen("Delete");
                        }}
                      />
                      <Edit
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedId(item?.id);
                          setOperation("Edit");
                          setQC(item);
                          handleOpen("Edit");
                        }}
                      />
                    </div>
                    <div className="qc-card-update-status">
                      <div className="qc-card-update-status-text">Status- </div>
                      <div className="qc-card-update-status-text">
                        <Box sx={{ width: 150 }}>
                          <FormControl fullWidth>
                            <Select
                              value={statuses[item.id] || "Select Status"}
                              onChange={(event) =>
                                handleStatusChange2(item, item.id, event)
                              }
                              sx={{
                                color:
                                  item?.status === "P"
                                    ? "rgba(239, 160, 41, 1)"
                                    : item?.status === "C"
                                    ? "green"
                                    : "black",
                              }}
                            >
                              <MenuItem value={"Select Status"}>
                                Select Status
                              </MenuItem>
                              <MenuItem value={"C"}>Required</MenuItem>
                              <MenuItem value={"NA"}>Not Required</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="qc-no-data">No control points found.</div>
        )}
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style }}>
          <XCircle
            onClick={handleClose}
            size={30}
            weight="fill"
            className="absolute cursor-pointer text-rgba-243-237-255 top-4 right-4"
          />
          {(operation === "Add" || operation === "Edit") && (
            <div>
              <div className="qc-card-add-title">
                {operation === "Add" ? "ADD CONTROL POINTS" : "EDIT CONTROL POINTS"}
              </div>
              <div className="qc-add-fields">
                <div className="qc-field">
                  <div className="qc-field-title">Control Point</div>
                  <TextField
                    name="controlPoint"
                    value={qc.controlPoint}
                    onChange={handleValueChange}
                    fullWidth
                    style={{
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  />
                </div>
                <div className="qc-field">
                  <div className="qc-field-title">Key Control</div>
                  <TextField
                    name="keycontrolsfor"
                    value={qc.keycontrolsfor}
                    onChange={handleValueChange}
                    fullWidth
                    style={{
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              </div>
              <div className="qc-add-desc">
                <div className="qc-add-textarea">
                  <div className="qc-textarea">
                    <div className="qc-field-title">
                      Control Point Description
                    </div>
                    <TextField
                      name="controlDescription"
                      value={qc.controlDescription}
                      onChange={handleValueChange}
                      multiline
                      rows={4}
                      maxRows={8}
                      fullWidth
                      style={{
                        background: "rgba(241, 241, 241, 1)",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="qc-add-dropdowns">
                <div className="qc-add-dropdown">
                  <div className="qc-field-title">TAT</div>
                  <TextField
                    name="tat"
                    value={qc.tat}
                    onChange={handleValueChange}
                    fullWidth
                    style={{
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  />
                </div>
                <div className="qc-add-dropdown">
                  <div className="qc-field-title">Accuracy</div>
                  <Box
                    sx={{
                      width: 400,
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    <FormControl fullWidth>
                      <Select
                        value={qc.accuracy}
                        onChange={(e) => handleDropdownChange(e, "accuracy")}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Enter Accuracy Value
                        </MenuItem>
                        <MenuItem value={"0%"}>0%</MenuItem>
                        <MenuItem value={"10%"}>10%</MenuItem>
                        <MenuItem value={"20%"}>20%</MenuItem>
                        <MenuItem value={"30%"}>30%</MenuItem>
                        <MenuItem value={"40%"}>40%</MenuItem>
                        <MenuItem value={"50%"}>50%</MenuItem>
                        <MenuItem value={"60%"}>60%</MenuItem>
                        <MenuItem value={"70%"}>70%</MenuItem>
                        <MenuItem value={"80%"}>80%</MenuItem>
                        <MenuItem value={"90%"}>90%</MenuItem>
                        <MenuItem value={"100%"}>100%</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </div>
                <div className="qc-add-dropdown">
                  <div className="qc-field-title">Frequency</div>
                  <Box
                    sx={{
                      width: 400,
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    <FormControl fullWidth>
                      <Select
                        value={qc.frequency}
                        onChange={(e) => handleDropdownChange(e, "frequency")}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Enter Frequency
                        </MenuItem>
                        <MenuItem value={"Daily"}>Daily</MenuItem>
                        <MenuItem value={"Weekly"}>Weekly</MenuItem>
                        <MenuItem value={"Monthly"}>Monthly</MenuItem>
                        <MenuItem value={"Bi-Monthly"}>Bi-Monthly</MenuItem>
                        <MenuItem value={"Quarterly"}>Quarterly</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </div>
                <div className="qc-add-dropdown">
                  <div className="qc-field-title">Criticality</div>
                  <Box
                    sx={{
                      width: 400,
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    <FormControl fullWidth>
                      <Select
                        value={qc.criticality}
                        onChange={(e) => handleDropdownChange(e, "criticality")}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Frequency Type
                        </MenuItem>
                        <MenuItem value={"High"}>High</MenuItem>
                        <MenuItem value={"Medium"}>Medium</MenuItem>
                        <MenuItem value={"Low"}>Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </div>
                <div className="qc-add-dropdown">
                  <div className="qc-field-title">Control Responsible</div>
                  <Box
                    sx={{
                      width: 400,
                      background: "rgba(241, 241, 241, 1)",
                      padding: "8px",
                      borderRadius: "8px",
                    }}
                  >
                    <FormControl fullWidth>
                      <Select
                        value={qc.controlResponsible}
                        onChange={(e) =>
                          handleDropdownChange(e, "controlResponsible")
                        }
                      >
                        {responsibleRoles?.map((role) => (
                          <MenuItem key={role.Id} value={role.Name}>
                            {role.Name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </div>
              </div>
              <div className="qc-add-buttons">
                <button onClick={handleClose}>Cancel</button>
                <button
                  disabled={
                    !qc.accuracy ||
                    !qc.tat ||
                    !qc.controlDescription ||
                    !qc.controlPoint ||
                    !qc.keycontrolsfor ||
                    !qc.frequency ||
                    !qc.criticality ||
                    !qc.controlResponsible
                  }
                  onClick={() => {
                    operation === "Add" ? addQc() : editQc();
                  }}
                  className="common_btn"
                >
                  {operation === "Add" ? "Add" : "Save"}
                </button>
              </div>
            </div>
          )}
          {operation === "Delete" && (
            <div>
              <div className="qc-card-add-title">
                Delete this Control Point?
              </div>
              <div
                className="qc-add-buttons"
                style={{ justifyContent: "center", gap: "50px" }}
              >
                <button onClick={handleClose}>Cancel</button>
                <button
                  onClick={async () => {
                    await editQcStatus2(qc, qc.id, "deleted");
                    handleClose();
                  }}
                  className="common_btn"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default ControlPointSetup;
